(ns practitest.frontend.integration-settings.azure-form
  (:require [practitest.frontend.init :as init]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [re-frame.core :as re-frame]
            [practitest.frontend.integration-settings.azure-entity-mapping.events :as events]
            [practitest.frontend.integration-settings.azure-entity-mapping.subs :as subs]
            [practitest.frontend.integration-settings.shared-form :as shared-form]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.selector2 :as selector2]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]))


(defn- get-available-type-options
  "Build dropdown options for a project's work item types, excluding types already
   selected for the other entity."
  [work-item-types current-mappings project-id entity-type other-entity-enabled?]
  (if-not work-item-types
    []
    (let [other-entity-type    (shared-form/get-other-entity-type entity-type)
          other-entity-map     (get-in current-mappings [(keyword project-id) other-entity-type])
          other-is-disabled?   (:disabled other-entity-map false)
          other-selected-types (if (or other-is-disabled? (not other-entity-enabled?))
                                 #{}
                                 (set (:azure_type other-entity-map [])))]
      (vec (remove #(contains? other-selected-types (:id %))
                   (map (fn [t] {:id (:id t) :label (:name t)}) work-item-types))))))


(defn- project-mapping-item
  [{:keys [project current-mappings entity-type other-entity-enabled?]}]
  (let [p-id-str        (name (:id project))
        work-item-types @(re-frame/subscribe [::subs/project-work-item-types])
        types-for-proj  (get work-item-types p-id-str)
        types-loading?  @(re-frame/subscribe [::subs/project-types-loading? p-id-str])
        entity-label    (shared-form/get-entity-label entity-type)
        mapping         (get current-mappings (keyword p-id-str))
        entity-map      (get mapping entity-type)
        is-disabled?    (:disabled entity-map false)
        type-options    (get-available-type-options types-for-proj current-mappings p-id-str
                                                    entity-type other-entity-enabled?)
        handle-change   (fn [types]
                          (mixpanel/track "Azure Integration Settings"
                                          {"Action" (str "Changed " entity-label " work item types")})
                          (re-frame/dispatch [::events/update-project-mapping p-id-str entity-type :azure_type types]))
        handle-toggle   (fn []
                          (mixpanel/track "Azure Integration Settings"
                                          {"Action" (str (if is-disabled? "Enable" "Disable")
                                                         " " entity-label " synchronization")})
                          (re-frame/dispatch [::events/toggle-project-entity-status p-id-str entity-type]))]
    [shared-form/project-mapping-row
     {:project-name    (:label project)
      :disabled?       is-disabled?
      :types-loading?  types-loading?
      :selected-types  (:azure_type entity-map [])
      :pt-entity       (shared-form/get-pt-entity-label entity-type)
      :type-options    type-options
      :placeholder     (format-translation (get-translation "bugtracker.integration-settings.select-item-types.placeholder"
                                                            "Select %{bugtracker} types")
                                           :bugtracker "Azure DevOps")
      :on-types-change handle-change
      :on-toggle       handle-toggle}]))


(defn- synchronization-section
  [{:keys [title enabled? other-entity-enabled? projects current-mappings on-toggle entity-type]}]
  [:div.sync-section
   [:div.sync-header
    [:div.sync-title
     [:h5 title]
     [tooltip/mux-tooltip
      {:position "top"}
      [:span (shared-form/get-sync-tooltip-text entity-type "Azure DevOps")]
      (fn [props]
        [:span.icon-info
         (assoc props :class "icon-info")
         [icons/mux-info-circle]])]]
    [shared-form/toggle-switch {:enabled?  enabled?
                                :on-change on-toggle}]]
   (when enabled?
     [:div.project-mappings
      (for [project projects]
        ^{:key (name (:id project))}
        [project-mapping-item
         {:project               project
          :current-mappings      current-mappings
          :entity-type           entity-type
          :other-entity-enabled? other-entity-enabled?}])])])

(defn- project-selector
  [{:keys [projects-for-selector selected-projects]}]
  [:div.form-group.integration-project-selector
   [:label (format-translation (get-translation "bugtracker.integration-settings.select-project"
                                                "Select %{bugtracker} Projects to Sync")
                               :bugtracker "Azure DevOps") ":"]
   [selector2/new-select
    {:id          "project-selector"
     :multi?      true
     :tags?       false
     :value       (shared-form/filter-selected-projects projects-for-selector selected-projects)
     :items       projects-for-selector
     :key-fn      :id
     :label-fn    :label
     :placeholder (str (get-translation "bugtracker.integration-settings.select-project.placeholder" "Select projects") "...")
     :on-change   (fn [selected-set]
                    (let [selected-ids (set (map :id selected-set))]
                      (mixpanel/track "Azure Integration Settings" {"Action" "Selected Azure Projects"})
                      (re-frame/dispatch [::events/update-selected-projects selected-ids])))}]])

(defn- general-settings-section
  [{:keys [allow-issue-creation? use-pt-id? skip-mapping?]}]
  [:div.general-settings
   [:h5 (get-translation "bugtracker.integration-settings.general-settings" "General Settings")]
   [:div.checkbox
    [:label
     [shared-form/checkbox
      {:checked?  allow-issue-creation?
       :on-change (fn [e]
                    (re-frame/dispatch [::events/toggle-allow-issue-creation (.. e -target -checked)]))}]
     (get-translation "bugtracker.integration-settings.checkbox.issue-creation"
                      "Allow to create Issues in PractiTest that are not linked to Integration")]]
   [:div.checkbox
    [:label
     [shared-form/checkbox
      {:disabled? (not allow-issue-creation?)
       :checked?  use-pt-id?
       :on-change (fn [e]
                    (re-frame/dispatch [::events/toggle-use-pt-id (.. e -target -checked)]))}]
     (get-translation "bugtracker.integration-settings.checkbox.use-pt-ids"
                      "Use PractiTest ID for synced Issues")]]
   [:div.checkbox
    [:label
     [shared-form/checkbox
      {:checked?  skip-mapping?
       :on-change (fn [e]
                    (re-frame/dispatch [::events/toggle-skip-mapping-manual-import (.. e -target -checked)]))}]
     (get-translation "bugtracker.integration-settings.checkbox.skip-mapping"
                      "Do not enforce mapping")
     " "
     [tooltip/mux-tooltip
      {:position "top"}
      [:span (format-translation (get-translation "bugtracker.integration-settings.skip-mapping.tooltip"
                                                   "Selecting this option will disable restrictions for mapped entities.
                                                   Any %{bugtracker} work item type can be synced as a requirement or an issue.")
                                 :bugtracker "Azure DevOps")]
      (fn [props]
        [:span.icon-info
         (assoc props :class "icon-info")
         [icons/mux-info-circle]])]]]])

(defn- form-actions []
  (let [saving? @(re-frame/subscribe [::subs/saving?])
        dirty?  @(re-frame/subscribe [::subs/dirty?])]
    [:div.form-actions
     [buttons/button
      {:text      (get-translation "generic.button.cancel" "Cancel")
       :disabled? (or saving? (not dirty?))
       :on-click  #(re-frame/dispatch [::events/cancel-changes])}]
     [buttons/button
      {:text      (if saving?
                    (str (get-translation "generic.label.saving" "Saving") "...")
                    (get-translation "jira-integration-settings.button.update-settings" "Update Settings"))
       :primary?  true
       :disabled? (or saving? (not dirty?))
       :on-click  #(re-frame/dispatch [::events/save-settings])}]]))

(defn azure-entity-mapping-form []
  (let [loading?              @(re-frame/subscribe [::subs/loading?])
        projects-loading?     @(re-frame/subscribe [::subs/projects-loading?])
        requirements-enabled? @(re-frame/subscribe [::subs/requirements-sync-enabled?])
        issues-enabled?       @(re-frame/subscribe [::subs/issues-sync-enabled?])
        available-projects    @(re-frame/subscribe [::subs/available-projects])
        projects-for-selector (vec (map shared-form/transform-project-for-selector available-projects))
        entity-mapping        @(re-frame/subscribe [::subs/data])
        current-mappings      (:project_mappings entity-mapping)
        selected-projects     @(re-frame/subscribe [::subs/selected-project-ids])
        allow-issue-creation? (:allow_issue_creation entity-mapping false)
        use-pt-id?            (:use_pt_id entity-mapping false)
        skip-mapping?         (:skip_mapping_in_manual_import entity-mapping false)
        integration-name      @(re-frame/subscribe [::subs/integration-name])
        selected-projects-set (shared-form/filter-selected-projects projects-for-selector selected-projects)]
    [:div.integration-form-content
     [:div.form-group.integration-env
      [:label [:strong (format-translation (get-translation "bugtracker.integration-settings.env"
                                                            "%{bugtracker} Environment")
                                           :bugtracker "Azure DevOps") ":"]]
      [:input.form-control.project-name-input {:type     "text"
                                               :value    (str integration-name)
                                               :disabled true}]]
     (if loading?
       [:div.loading-container
        [spinners/mux-spinner {:size :medium}]]
       [:<>
        (if projects-loading?
          [:div.loading-container.projects-loading
           [spinners/mux-spinner {:size :small}]]
          [:<>
           [project-selector
            {:projects-for-selector projects-for-selector
             :selected-projects     selected-projects}]
           [synchronization-section
            {:title                 (get-translation "bugtracker.integration-settings.requirements-synchronization.title"
                                                     "Enable Requirement Synchronization")
             :enabled?              requirements-enabled?
             :other-entity-enabled? issues-enabled?
             :on-toggle             #(re-frame/dispatch [::events/toggle-requirements-sync %])
             :projects              selected-projects-set
             :current-mappings      current-mappings
             :entity-type           :requirements}]
           [synchronization-section
            {:title                 (get-translation "bugtracker.integration-settings.issues-synchronization.title"
                                                     "Enable Issue Synchronization")
             :enabled?              issues-enabled?
             :other-entity-enabled? requirements-enabled?
             :on-toggle             #(re-frame/dispatch [::events/toggle-issues-sync %])
             :projects              selected-projects-set
             :current-mappings      current-mappings
             :entity-type           :issues}]])
        [general-settings-section
         {:allow-issue-creation? allow-issue-creation?
          :use-pt-id?            use-pt-id?
          :skip-mapping?         skip-mapping?}]
        [form-actions]])]))

(defn azure-integration-form-root [_]
  (fn [_]
    [azure-entity-mapping-form]))

(defn ^:export init [webcomponent options]
  (init/init! {:options       options
               :init-event-fn (fn [config]
                                [[::events/initialize-db config]
                                 [::events/fetch-entity-mapping]
                                 [::events/fetch-available-projects]])
               :webcomponent  webcomponent
               :root-view     azure-integration-form-root}))

(defn ^:dev/after-load remount []
  (init/remount-roots "azure-integration-form-component"
                      azure-integration-form-root))

(utils/register-webcomponent! "azure-integration-form-component")
